/**
 * Electrical subservice flow: starts with electrical-install / electrical-repair / electrical-inspection
 * (Cosmos catalog subservices), plus customer-only paths for urgent-electrical and not-sure.
 */
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import PowerIcon from '@mui/icons-material/Power';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HomeIcon from '@mui/icons-material/Home';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LightIcon from '@mui/icons-material/Light';
import SwitchRightIcon from '@mui/icons-material/SwitchRight';
import OutdoorGrillIcon from '@mui/icons-material/OutdoorGrill';
import KitchenIcon from '@mui/icons-material/Kitchen';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SecurityIcon from '@mui/icons-material/Security';
import EvStationIcon from '@mui/icons-material/EvStation';
import SmokeFreeIcon from '@mui/icons-material/SmokeFree';
import BuildIcon from '@mui/icons-material/Build';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import BathtubIcon from '@mui/icons-material/Bathtub';
import BedIcon from '@mui/icons-material/Bed';
import WeekendIcon from '@mui/icons-material/Weekend';
import FoundationIcon from '@mui/icons-material/Foundation';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import LocalLaundryServiceIcon from '@mui/icons-material/LocalLaundryService';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import LayersIcon from '@mui/icons-material/Layers';
import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import PropaneTankIcon from '@mui/icons-material/PropaneTank';

// ─── Step 1: Category (first three align with Cosmos subservices; urgent / not-sure are wizard-only) ─
const ELECTRICAL_PROBLEMS = [
  { id: 'electrical-install', label: 'Installations & upgrades', icon: AddCircleOutlineIcon },
  { id: 'electrical-repair', label: 'Repairs & troubleshooting', icon: PowerOffIcon },
  { id: 'electrical-inspection', label: 'Inspections & assessments', icon: FactCheckIcon },
  { id: 'urgent-electrical', label: 'Urgent electrical problem (safety concern)', icon: ReportProblemIcon, urgent: true },
  { id: 'not-sure', label: 'Not sure — I need an electrician to check the issue', icon: HelpOutlineIcon },
];

// ─── Branch: Install options ───────────────────────────────────────────────
const INSTALL_OPTIONS = [
  { id: 'install-outlet', label: 'Install or Add an Electrical Outlet', icon: AddCircleOutlineIcon },
  { id: 'install-ceiling-fan', label: 'Install a Ceiling Fan or Light Fixture', icon: LightIcon },
  { id: 'install-switch-dimmer', label: 'Install or Replace a Light Switch or Dimmer', icon: SwitchRightIcon },
  { id: 'install-outdoor-lighting', label: 'Install Outdoor or Security Lighting', icon: OutdoorGrillIcon },
  { id: 'install-dedicated-circuit', label: 'Install a Dedicated Circuit for Appliance', icon: KitchenIcon },
  { id: 'panel-upgrade', label: 'Electrical Panel Upgrade or Replacement', icon: UpgradeIcon },
  { id: 'ev-charger', label: 'EV Charger Installation', icon: EvStationIcon },
  { id: 'smoke-co-detector', label: 'Smoke or Carbon Monoxide Detector Install', icon: SmokeFreeIcon },
  { id: 'generator-install', label: 'Generator Installation', icon: OfflineBoltIcon },
];

// ─── Branch: Electric Not Working options ───────────────────────────────────
const REPAIR_OPTIONS = [
  { id: 'outlet-not-working', label: 'Outlet Not Working or Sparking', icon: PowerIcon },
  { id: 'light-switch-not-working', label: 'Light Switch Not Working', icon: SwitchRightIcon },
  { id: 'breaker-tripping', label: 'Breaker Keeps Tripping', icon: PowerOffIcon },
  { id: 'power-out-partial', label: 'Power Out in Part of the House', icon: PowerOffIcon },
  { id: 'lights-flickering', label: 'Lights Flickering or Dimming', icon: LightbulbIcon },
  { id: 'urgent-electrical', label: 'Urgent Electrical Problem (Safety Concern)', icon: ReportProblemIcon, urgent: true },
  { id: 'not-sure', label: 'Not sure — I need an electrician to check the issue', icon: HelpOutlineIcon },
];

// ─── Branch: Inspection options ───────────────────────────────────────────
const INSPECTION_OPTIONS = [
  { id: 'safety-inspection', label: 'Home Electrical Safety Inspection', icon: FactCheckIcon },
  { id: 'panel-assessment', label: 'Electrical Panel Assessment', icon: UpgradeIcon },
];

// ─── Follow-up: Install — new outlets ───────────────────────────────────────
const OUTLET_COUNT_OPTIONS = [
  { id: 'one', label: '1 outlet', icon: FormatListNumberedIcon },
  { id: 'two', label: '2 outlets', icon: FormatListNumberedIcon },
  { id: 'three', label: '3 outlets', icon: FormatListNumberedIcon },
  { id: 'four', label: '4 outlets', icon: FormatListNumberedIcon },
  { id: 'five-plus', label: '5 or more', icon: FormatListNumberedIcon },
];

const ROOM_LOCATION_OPTIONS = [
  { id: 'kitchen', label: 'Kitchen', icon: KitchenIcon },
  { id: 'bathroom', label: 'Bathroom', icon: BathtubIcon },
  { id: 'bedroom', label: 'Bedroom', icon: BedIcon },
  { id: 'living-room', label: 'Living room', icon: WeekendIcon },
  { id: 'basement', label: 'Basement', icon: FoundationIcon },
  { id: 'garage', label: 'Garage', icon: LocalParkingIcon },
  { id: 'hallway', label: 'Hallway', icon: MeetingRoomIcon },
  { id: 'laundry', label: 'Laundry / utility', icon: LocalLaundryServiceIcon },
  { id: 'office', label: 'Office', icon: BusinessCenterIcon },
  { id: 'multiple-rooms', label: 'Multiple rooms', icon: LayersIcon },
  { id: 'other-room', label: 'Other', icon: HelpOutlineIcon },
];

const CEILING_FAN_ROOM_OPTIONS = ROOM_LOCATION_OPTIONS;

// ─── Follow-up: Switch / dimmer ────────────────────────────────────────────
const SWITCH_JOB_OPTIONS = [
  { id: 'new-install', label: 'New install (no existing switch)', icon: AddCircleOutlineIcon },
  { id: 'replace-switch', label: 'Replace existing switch', icon: SwitchRightIcon },
  { id: 'add-dimmer', label: 'Add or replace with dimmer', icon: LightbulbIcon },
  { id: 'multi-location', label: '3-way / multi-location wiring', icon: LayersIcon },
];

// ─── Follow-up: Outdoor lighting ───────────────────────────────────────────
const OUTDOOR_LIGHTING_OPTIONS = [
  { id: 'front-entrance', label: 'Front entrance / porch', icon: HomeIcon },
  { id: 'back-yard', label: 'Back yard / patio / deck', icon: OutdoorGrillIcon },
  { id: 'side-yard', label: 'Side of house / driveway', icon: LocalParkingIcon },
  { id: 'soffit-eaves', label: 'Soffit / eaves / under roofline', icon: MeetingRoomIcon },
  { id: 'motion-security', label: 'Motion-activated / security flood', icon: SecurityIcon },
  { id: 'landscape-path', label: 'Landscape / path / accent', icon: LightIcon },
  { id: 'outdoor-multiple', label: 'Multiple areas', icon: LayersIcon },
  { id: 'outdoor-other', label: 'Other', icon: HelpOutlineIcon },
];

// ─── Follow-up: Panel upgrade ───────────────────────────────────────────────
const PANEL_CURRENT_AMPS_OPTIONS = [
  { id: 'amps-60', label: '60A or less (older home)', icon: PowerIcon },
  { id: 'amps-100', label: '100A', icon: PowerIcon },
  { id: 'amps-125-150', label: '125–150A', icon: PowerIcon },
  { id: 'amps-200', label: '200A (already upgraded)', icon: PowerIcon },
  { id: 'amps-unknown', label: 'Not sure', icon: HelpOutlineIcon },
];

const PANEL_UPGRADE_REASON_OPTIONS = [
  { id: 'need-capacity', label: 'Need more capacity (EV, AC, additions)', icon: EvStationIcon },
  { id: 'safety-fuses', label: 'Safety — fuses or outdated panel', icon: ReportProblemIcon },
  { id: 'remodel-circuits', label: 'Remodel / more circuits', icon: BuildIcon },
  { id: 'insurance-inspection', label: 'Insurance or inspection requirement', icon: FactCheckIcon },
  { id: 'panel-reason-other', label: 'Other', icon: HelpOutlineIcon },
];

// ─── Follow-up: Smoke / CO ─────────────────────────────────────────────────
const SMOKE_CO_PRODUCT_OPTIONS = [
  { id: 'combo', label: 'Combination smoke & CO', icon: SmokeFreeIcon },
  { id: 'smoke-only', label: 'Smoke detectors only', icon: SmokeFreeIcon },
  { id: 'co-only', label: 'Carbon monoxide only', icon: ReportProblemIcon },
  { id: 'replace-existing', label: 'Replace existing units', icon: BuildIcon },
];

const SMOKE_CO_COUNT_OPTIONS = [
  { id: 'smoke-count-1', label: '1 unit', icon: FormatListNumberedIcon },
  { id: 'smoke-count-2', label: '2 units', icon: FormatListNumberedIcon },
  { id: 'smoke-count-3', label: '3 units', icon: FormatListNumberedIcon },
  { id: 'smoke-count-4', label: '4 units', icon: FormatListNumberedIcon },
  { id: 'smoke-count-5-plus', label: '5 or more', icon: FormatListNumberedIcon },
];

const HARDWIRED_VS_BATTERY_OPTIONS = [
  { id: 'hardwired', label: 'Hardwired (existing or new)', icon: ElectricalServicesIcon },
  { id: 'battery', label: 'Battery-powered', icon: PowerIcon },
  { id: 'hardwired-battery-backup', label: 'Hardwired with battery backup', icon: SecurityIcon },
  { id: 'wiring-not-sure', label: 'Not sure', icon: HelpOutlineIcon },
];

// ─── Follow-up: Repairs — which area / detail ───────────────────────────────
const BREAKER_TRIPPING_AREA_OPTIONS = [
  { id: 'whole-house', label: 'Whole house / main breaker', icon: HomeIcon },
  { id: 'kitchen-circuit', label: 'Kitchen', icon: KitchenIcon },
  { id: 'bathroom-circuit', label: 'Bathroom', icon: BathtubIcon },
  { id: 'basement-circuit', label: 'Basement', icon: FoundationIcon },
  { id: 'garage-circuit', label: 'Garage', icon: LocalParkingIcon },
  { id: 'hvac-circuit', label: 'HVAC / AC / furnace', icon: UpgradeIcon },
  { id: 'breaker-multiple', label: 'Multiple breakers trip', icon: LayersIcon },
  { id: 'breaker-unknown', label: 'Not sure which circuit', icon: HelpOutlineIcon },
];

const PARTIAL_OUTAGE_AREA_OPTIONS = [
  { id: 'outage-one-room', label: 'Single room only', icon: MeetingRoomIcon },
  { id: 'outage-one-floor', label: 'One floor of the home', icon: WeekendIcon },
  { id: 'outage-kitchen-bath', label: 'Kitchen or bath only', icon: KitchenIcon },
  { id: 'outage-basement', label: 'Basement / crawl', icon: FoundationIcon },
  { id: 'outage-garage', label: 'Garage / outbuilding', icon: LocalParkingIcon },
  { id: 'outage-half-house', label: 'Roughly half the house', icon: HomeIcon },
  { id: 'outage-other-area', label: 'Other', icon: HelpOutlineIcon },
];

// ─── Follow-up: Inspection — motivation ─────────────────────────────────────
const INSPECTION_MOTIVATION_OPTIONS = [
  { id: 'buying-home', label: 'Buying a home (pre-purchase)', icon: HomeIcon },
  { id: 'selling-home', label: 'Selling / listing', icon: FactCheckIcon },
  { id: 'insurance-requirement', label: 'Insurance requirement', icon: SecurityIcon },
  { id: 'renovation', label: 'Before or after renovation', icon: BuildIcon },
  { id: 'safety-concern', label: 'Safety concern / something seems wrong', icon: ReportProblemIcon },
  { id: 'routine-check', label: 'Routine peace of mind', icon: FactCheckIcon },
  { id: 'inspection-motivation-other', label: 'Other', icon: HelpOutlineIcon },
];

// ─── Follow-up: EV charger install location ─────────────────────────────────
const EV_INSTALL_LOCATION_OPTIONS = [
  { id: 'attached-garage', label: 'Attached garage', icon: LocalParkingIcon },
  { id: 'detached-garage', label: 'Detached garage', icon: HomeIcon },
  { id: 'carport', label: 'Carport / covered parking', icon: OutdoorGrillIcon },
  { id: 'driveway-exterior', label: 'Exterior wall (driveway)', icon: ElectricalServicesIcon },
  { id: 'basement-level', label: 'Basement / lower level', icon: FoundationIcon },
  { id: 'ev-other', label: 'Other / not sure yet', icon: HelpOutlineIcon },
];

// ─── Follow-up: Dedicated circuit — which appliance ─────────────────────────
const DEDICATED_APPLIANCE_OPTIONS = [
  { id: 'dryer', label: 'Dryer', icon: LocalLaundryServiceIcon },
  { id: 'range-oven', label: 'Range / oven', icon: KitchenIcon },
  { id: 'microwave', label: 'Built-in microwave', icon: KitchenIcon },
  { id: 'ev-charger-appliance', label: 'EV charger', icon: EvStationIcon },
  { id: 'hot-tub', label: 'Hot tub / spa', icon: BathtubIcon },
  { id: 'ac-unit', label: 'AC condenser / large HVAC', icon: UpgradeIcon },
  { id: 'dedicated-other', label: 'Other appliance', icon: HelpOutlineIcon },
];

// ─── Follow-up: Generator install ───────────────────────────────────────────
const GENERATOR_TYPE_OPTIONS = [
  { id: 'standby-generator', label: 'Standby (permanent, automatic backup)', icon: HomeIcon },
  { id: 'portable-generator', label: 'Portable (manual, plug-in backup)', icon: Inventory2Icon },
];

const GENERATOR_FUEL_OPTIONS = [
  { id: 'natural-gas', label: 'Natural gas', icon: LocalGasStationIcon },
  { id: 'propane', label: 'Propane', icon: PropaneTankIcon },
  { id: 'fuel-not-sure', label: 'Not sure yet', icon: HelpOutlineIcon },
];

const URGENCY_OPTIONS = [
  { id: 'asap', label: 'As soon as possible', icon: WarningAmberIcon, urgent: true },
  { id: 'today', label: 'Today', icon: TodayIcon },
  { id: 'few-days', label: 'Within the next few days', icon: DateRangeIcon },
  { id: 'just-quotes', label: 'Just getting quotes', icon: RequestQuoteIcon },
];

// ─── Flow branches: one step per category ───────────────────────────────────
const FLOW_BRANCHES = {
  'electrical-install': [
    {
      key: 'installType',
      title: 'What do you want to install?',
      subtitle: 'Select the type of installation you need.',
      icon: BuildIcon,
      options: INSTALL_OPTIONS,
    },
    {
      key: 'outletCount',
      title: 'How many outlets?',
      subtitle: 'Tell us the scope so we can plan the job.',
      icon: FormatListNumberedIcon,
      options: OUTLET_COUNT_OPTIONS,
      condition: (fd) => fd.installType === 'install-outlet',
    },
    {
      key: 'outletRoomLocation',
      title: 'Where in the home?',
      subtitle: 'Pick the main room or area for this work.',
      icon: HomeIcon,
      options: ROOM_LOCATION_OPTIONS,
      condition: (fd) => fd.installType === 'install-outlet',
    },
    {
      key: 'fanRoomLocation',
      title: 'Where will the fan or fixture go?',
      subtitle: 'Which room or area should we plan for?',
      icon: LightIcon,
      options: CEILING_FAN_ROOM_OPTIONS,
      condition: (fd) => fd.installType === 'install-ceiling-fan',
    },
    {
      key: 'switchRoomLocation',
      title: 'Which room for the switch or dimmer?',
      subtitle: 'Room type affects box depth, neutral needs, and multi-way wiring.',
      icon: MeetingRoomIcon,
      options: ROOM_LOCATION_OPTIONS,
      condition: (fd) => fd.installType === 'install-switch-dimmer',
    },
    {
      key: 'switchJobType',
      title: 'What kind of switch work?',
      subtitle: 'New install, replacement, dimmer, or multi-location (3-way) wiring.',
      icon: SwitchRightIcon,
      options: SWITCH_JOB_OPTIONS,
      condition: (fd) => fd.installType === 'install-switch-dimmer',
    },
    {
      key: 'outdoorLightingScope',
      title: 'Where do you need outdoor lighting?',
      subtitle: 'Pick the main area — you can add more detail in the next step.',
      icon: OutdoorGrillIcon,
      options: OUTDOOR_LIGHTING_OPTIONS,
      condition: (fd) => fd.installType === 'install-outdoor-lighting',
    },
    {
      key: 'panelCurrentAmps',
      title: "What's your current panel size?",
      subtitle: 'A rough guess helps — your electrician can verify on site.',
      icon: PowerIcon,
      options: PANEL_CURRENT_AMPS_OPTIONS,
      condition: (fd) => fd.installType === 'panel-upgrade',
    },
    {
      key: 'panelUpgradeReason',
      title: 'Why are you upgrading the panel?',
      subtitle: 'More capacity, safety, remodel, or insurance — it shapes the scope.',
      icon: UpgradeIcon,
      options: PANEL_UPGRADE_REASON_OPTIONS,
      condition: (fd) => fd.installType === 'panel-upgrade',
    },
    {
      key: 'evInstallLocation',
      title: 'Where will the EV charger go?',
      subtitle: 'Typical installs are in or near the garage.',
      icon: EvStationIcon,
      options: EV_INSTALL_LOCATION_OPTIONS,
      condition: (fd) => fd.installType === 'ev-charger',
    },
    {
      key: 'dedicatedAppliance',
      title: 'Which appliance needs the circuit?',
      subtitle: 'Different loads need different wire and breaker sizes.',
      icon: KitchenIcon,
      options: DEDICATED_APPLIANCE_OPTIONS,
      condition: (fd) => fd.installType === 'install-dedicated-circuit',
    },
    {
      key: 'smokeCoProduct',
      title: 'What do you need installed?',
      subtitle: 'Smoke only, CO only, combo units, or replacing existing alarms.',
      icon: SmokeFreeIcon,
      options: SMOKE_CO_PRODUCT_OPTIONS,
      condition: (fd) => fd.installType === 'smoke-co-detector',
    },
    {
      key: 'smokeCoCount',
      title: 'How many units?',
      subtitle: 'Approximate count is fine for quoting.',
      icon: FormatListNumberedIcon,
      options: SMOKE_CO_COUNT_OPTIONS,
      condition: (fd) => fd.installType === 'smoke-co-detector',
    },
    {
      key: 'smokeCoPowerType',
      title: 'Hardwired or battery-powered?',
      subtitle: 'Existing hardwired alarms may need compatible replacements.',
      icon: ElectricalServicesIcon,
      options: HARDWIRED_VS_BATTERY_OPTIONS,
      condition: (fd) => fd.installType === 'smoke-co-detector',
    },
    {
      key: 'generatorType',
      title: 'What kind of generator?',
      subtitle: 'Standby units run automatically; portable units are manual backup power.',
      icon: OfflineBoltIcon,
      options: GENERATOR_TYPE_OPTIONS,
      condition: (fd) => fd.installType === 'generator-install',
    },
    {
      key: 'generatorFuelType',
      title: 'What fuel source will it use?',
      subtitle: 'Fuel type affects sizing, hookup, and permitting.',
      icon: LocalGasStationIcon,
      options: GENERATOR_FUEL_OPTIONS,
      condition: (fd) => fd.installType === 'generator-install',
    },
  ],
  'electrical-repair': [
    {
      key: 'repairType',
      title: "What's not working?",
      subtitle: 'Select the issue that best describes your situation.',
      icon: PowerOffIcon,
      options: REPAIR_OPTIONS,
    },
    {
      key: 'repairOutletRoom',
      title: 'Which room has the bad outlet?',
      subtitle: 'Helps your electrician bring the right tools and parts.',
      icon: MeetingRoomIcon,
      options: ROOM_LOCATION_OPTIONS,
      condition: (fd) => fd.repairType === 'outlet-not-working',
    },
    {
      key: 'repairSwitchRoom',
      title: 'Which room has the switch problem?',
      subtitle: 'Switch location affects whether it’s a switch, wiring, or fixture issue.',
      icon: MeetingRoomIcon,
      options: ROOM_LOCATION_OPTIONS,
      condition: (fd) => fd.repairType === 'light-switch-not-working',
    },
    {
      key: 'breakerTrippingArea',
      title: 'Where is the breaker tripping?',
      subtitle: 'Kitchen, bath, HVAC, and garage circuits are common culprits.',
      icon: ElectricalServicesIcon,
      options: BREAKER_TRIPPING_AREA_OPTIONS,
      condition: (fd) => fd.repairType === 'breaker-tripping',
    },
    {
      key: 'powerOutLocation',
      title: 'How much of the home is affected?',
      subtitle: 'Partial outages often point to a single branch circuit or loose neutral.',
      icon: HomeIcon,
      options: PARTIAL_OUTAGE_AREA_OPTIONS,
      condition: (fd) => fd.repairType === 'power-out-partial',
    },
    {
      key: 'flickeringLocation',
      title: 'Where do you notice flickering or dimming?',
      subtitle: 'One room vs. whole house helps narrow down the cause.',
      icon: LightbulbIcon,
      options: ROOM_LOCATION_OPTIONS,
      condition: (fd) => fd.repairType === 'lights-flickering',
    },
  ],
  'electrical-inspection': [
    {
      key: 'inspectionType',
      title: 'What kind of inspection?',
      subtitle: 'We can help with safety checks and panel assessments.',
      icon: FactCheckIcon,
      options: INSPECTION_OPTIONS,
    },
    {
      key: 'inspectionMotivation',
      title: 'What brings you in?',
      subtitle: 'Buying, selling, insurance, renovation, or a safety check — we’ll match the right pro.',
      icon: FactCheckIcon,
      options: INSPECTION_MOTIVATION_OPTIONS,
    },
  ],
  /** No branch steps — go straight to tail (urgent uses urgentTailKeys below). */
  'urgent-electrical': [],
  /** No branch steps — full tail including urgency. */
  'not-sure': [],
};

const SHARED_TAIL_KEYS = ['urgency', 'photos', 'address', 'review'];
const URGENT_TAIL_KEYS = ['photos', 'address', 'review'];

const STEP_LABELS = {
  problem: {
    title: 'What do you need?',
    subtitle: 'Choose a category to get started.',
    icon: ElectricalServicesIcon,
  },
  urgency: {
    title: 'When do you need help?',
    subtitle: "Let us know your timeline so we can prioritize.",
    icon: ScheduleIcon,
  },
  photos: {
    title: 'Photos & details',
    subtitle: 'Optional — but photos help electricians give better estimates.',
    icon: AddPhotoAlternateIcon,
  },
  address: {
    title: 'What city do you live in?',
    subtitle:
      'Enter your city and state so we can match you with nearby pros. No street address needed.',
    icon: HomeIcon,
  },
  review: {
    title: 'Review your request',
    subtitle: "Make sure everything looks good before we find your electrician.",
    icon: CheckCircleOutlineIcon,
  },
};

const ILLUSTRATION_CAPTIONS = {
  problem: "Tell us what you need and we'll connect you with the right local electrician.",
  installType: 'Installations are one of the most common requests — we’ll match you with a qualified pro.',
  outletCount: 'Knowing how many outlets helps your electrician quote materials and labor.',
  outletRoomLocation: 'Room location affects routing, wall type, and whether new circuits are needed.',
  fanRoomLocation: 'Ceiling height and box type vary by room — this helps us match the right pro.',
  switchRoomLocation: 'Room type tells pros if you need neutral wires, deeper boxes, or multi-way switches.',
  switchJobType: 'Dimmer, 3-way, or new rough-in — each needs different wiring and parts.',
  outdoorLightingScope: 'Front, soffit, and landscape runs have different mounting and GFCI needs.',
  panelCurrentAmps: 'Service size drives whether you need a utility upgrade or just a new panel.',
  panelUpgradeReason: 'EV, AC, and additions often need more capacity — we’ll match someone who does load calcs.',
  evInstallLocation: 'EV charger placement affects wire runs, panel capacity, and permits.',
  dedicatedAppliance: 'Appliance type determines amperage and whether a subpanel upgrade is needed.',
  smokeCoProduct: 'Combo vs. separate units and code requirements vary by location — pros can advise.',
  smokeCoCount: 'Bedrooms, hallways, and basements each may need coverage — count helps with quoting.',
  smokeCoPowerType: 'Hardwired alarms often need compatible replacements and may tie to AC power.',
  generatorType: 'Standby generators need a transfer switch and permitting; portable units are simpler.',
  generatorFuelType: 'Natural gas and propane are the most common fuel sources for home standby generators.',
  repairType: 'We prioritize safety. Describe the issue and we’ll find an electrician who can help.',
  repairOutletRoom: 'Room and outlet age help pros guess GFCI needs and box conditions.',
  repairSwitchRoom: 'Location narrows down switch vs. fixture vs. circuit issues before they arrive.',
  breakerTrippingArea: 'Which circuit trips points to overloads, shorts, or appliance faults.',
  powerOutLocation: 'Partial outages vs. one room helps distinguish branch problems from service issues.',
  flickeringLocation: 'Whole-house flicker can mean utility or main; one room often means a loose connection.',
  inspectionType: 'Inspections help keep your home safe and can uncover issues before they become problems.',
  inspectionMotivation: 'Pre-purchase and insurance inspections focus on different checklist items.',
  urgency: 'We prioritize urgent requests and match availability to your timeline.',
  photos: 'A picture helps electricians understand the job before they arrive.',
  address: "We'll use your city and state to find electricians who service your area.",
  review: "Almost there! Review your info and we'll find the best local pros.",
};

/** Electrical question config (saved-request display; see wizardServiceRegistry) */
export const electricalSubserviceFlowConfig = {
  problemStep: {
    options: ELECTRICAL_PROBLEMS,
    ...STEP_LABELS.problem,
  },
  flowBranches: FLOW_BRANCHES,
  sharedTailKeys: SHARED_TAIL_KEYS,
  urgentTailKeys: URGENT_TAIL_KEYS,
  urgentProblemId: 'urgent-electrical',
  isUrgentFormData: (formData) =>
    formData.problem === 'urgent-electrical' || formData.repairType === 'urgent-electrical',
  urgentFieldValues: { repairType: 'urgent-electrical' },
  urgencyStep: {
    options: URGENCY_OPTIONS,
    ...STEP_LABELS.urgency,
  },
  photosStep: STEP_LABELS.photos,
  addressStep: STEP_LABELS.address,
  reviewStep: STEP_LABELS.review,
  illustrationCaptions: ILLUSTRATION_CAPTIONS,
  submitButtonLabel: 'Find my electrician',
};
